import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Button } from 'reactstrap'
import {
  mapSasbossEnterprise,
  searchSasbossCompanies,
} from '../../actions/sasbossDidActions'

// Debounced type-ahead over CW companies (GET /sasboss/companies/search). Clicking a row maps the
// SASBOSS enterprise to that CW company. Mirrors the CSP TenantMappingPicker; the search rows are
// CWCompanySearchResultDto ({ id, name, identifier, statusName }).

const useDebounced = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(handle)
  }, [value, delayMs])
  return debounced
}

const SasbossCompanyPicker = ({
  enterpriseId,
  enterpriseName,
  submitLabel = 'Map',
  onMapped,
  onCancel,
}) => {
  const dispatch = useDispatch()
  const updatingEnterpriseId = useSelector((s) => s.sasbossDid.updatingEnterpriseId)
  const isUpdating = updatingEnterpriseId === enterpriseId

  const [searchText, setSearchText] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [pickedCwId, setPickedCwId] = useState(null)
  const debounced = useDebounced(searchText, 250)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!debounced.trim()) { setResults([]); setHasSearched(false); return }
      setSearching(true)
      const rows = await dispatch(searchSasbossCompanies(debounced, 20))
      if (!cancelled) {
        setResults(rows || [])
        setSearching(false)
        setHasSearched(true)
      }
    }
    run()
    return () => { cancelled = true }
  }, [dispatch, debounced])

  const handleMap = async () => {
    if (!pickedCwId || !enterpriseId) return
    await dispatch(mapSasbossEnterprise(enterpriseId, enterpriseName, pickedCwId))
    onMapped?.()
  }

  return (
    <div className="d-flex" style={{ gap: '0.5rem' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <Input
          type="text"
          placeholder="Search companies..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPickedCwId(null) }}
          bsSize="sm"
          disabled={isUpdating}
        />
        {searchText && !pickedCwId && (searching || hasSearched) && (
          <div
            className="border rounded bg-white shadow-sm"
            style={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: '280px', overflowY: 'auto' }}
          >
            {searching ? (
              <div className="p-2 small text-muted">
                <span className="spinner-border spinner-border-sm mr-2" />
                Searching...
              </div>
            ) : results.length === 0 ? (
              <div className="p-2 small text-muted">No matches.</div>
            ) : (
              results.map((c) => (
                <div
                  key={c.id}
                  className="p-2 border-bottom"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPickedCwId(c.id)
                    setSearchText(c.name)
                  }}
                >
                  <small>
                    <strong>{c.name}</strong>
                    {c.identifier && <span className="text-muted ml-2">#{c.identifier}</span>}
                    {c.statusName && <span className="text-muted ml-2">({c.statusName})</span>}
                  </small>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <Button
        size="sm"
        color="primary"
        onClick={handleMap}
        disabled={!pickedCwId || isUpdating}
      >
        {isUpdating ? <span className="spinner-border spinner-border-sm" /> : submitLabel}
      </Button>
      {onCancel && (
        <Button size="sm" color="outline-secondary" onClick={onCancel} disabled={isUpdating}>
          Cancel
        </Button>
      )}
    </div>
  )
}

export default SasbossCompanyPicker
